import { ReactNode, useEffect } from 'react';
import { useSetupFlowStep } from 'accounts/hooks/homeowners/use-setup-flow-step-context';
import { useStepFormLayout } from 'accounts/hooks/homeowners/use-step-form-layout';
import { animationsCache } from 'animations/animationsCache';
import Form from 'components/Form/Form';
import { ProgressBar } from 'components/ProgressBar/ProgressBar';
import { UnitAddressTitle } from 'components/UnitAddressTitle/UnitAddressTitle';
import Space, { SPACE_TYPES } from 'corecomponents/Space/Space';
import { findLastIndex, isEqual } from 'es-toolkit/compat';
import arrayMutators from 'final-form-arrays';
import FormLayout from 'layouts/FormLayout/FormLayout';
import { trackMixpanelEvent } from '@belong/common';
import { FormRenderProps } from 'react-final-form';

type StepFormLayoutProps = {
  title: string;
  onSubmit: (values: Record<string, unknown>) => void;
  /**
   * A render prop, not a callback: `Form` calls it as `getForm({ ...formProps })`
   * with react-final-form's render props, and every step destructures `form` and
   * `values` off it. Typing the parameter as the `FormApi` alone — which is only
   * the `form` member of that object — is what made those reads errors.
   */
  getForm?: (formProps: FormRenderProps) => ReactNode;
  preTitle?: ReactNode;
  subTitle?: string | ReactNode;
  initialValues?: Record<string, unknown>;
  mutators?: Record<string, unknown>;
  form?: ReactNode;
  // Both default to `null` below, which is what `Form` expects for "not set".
  customValidation?:
    | ((values: Record<string, unknown>) => Record<string, unknown>)
    | null;
  ctaProps?: Record<string, unknown>;
  showUnitAddress?: boolean;
  bannerStep?: ReactNode;
  nextLabel?: string;
  doneLabel?: string;
  customError?: string | null;
  handleBackStep?: () => void;
  disableProgressBar?: boolean;
  /**
   * Blocks the progress bar's steps *after* the current one, leaving earlier steps
   * navigable. For steps that can't be completed yet, where `disableProgressBar`
   * (which blocks every direction) would also strand the homeowner.
   */
  blockForwardProgress?: boolean;
};

const { FormLayoutHeader } = FormLayout;

export function StepFormLayout({
  initialValues = {},
  mutators = {},
  subTitle = '',
  form = null,
  customValidation = null,
  ctaProps = {},
  preTitle,
  title,
  showUnitAddress = true,
  getForm,
  onSubmit,
  bannerStep,
  nextLabel = 'Next',
  doneLabel = 'Done',
  customError = null,
  disableProgressBar,
  blockForwardProgress,
  handleBackStep,
}: StepFormLayoutProps) {
  const {
    isLastStep,
    units,
    selectedUnit,
    selectedProperty,
    loading,
    currentStep,
    propertyId,
    unitId,
    stepsByGate,
  } = useSetupFlowStep();

  const { progressItems, loading: stepFormLayout } = useStepFormLayout({
    currentStep,
    propertyId,
    unitId,
    stepsByGate,
  });

  const unitAddress = `${selectedProperty?.address.streetAddress} Unit ${selectedUnit?.basicInfo.unitNumber}`;

  // Matches how `ProgressBar` itself derives the current step, so the cutoff for
  // `blockForwardProgress` lines up with the filled portion of the bar.
  const activeIndex = findLastIndex(progressItems, (item) =>
    Boolean(item.active),
  );

  const items = progressItems?.map((progressItem, index) => ({
    ...progressItem,
    disabled:
      disableProgressBar ||
      (blockForwardProgress && activeIndex >= 0 && index > activeIndex),
  }));

  useEffect(() => {
    animationsCache.preload('lock_unlock');

    if (currentStep) {
      trackMixpanelEvent('flow-step-viewed', {
        flowType: 'HomeownerSetup',
        stepEnum: currentStep.stepName,
        stepDisplayName: currentStep.displayName || title,
        homeId: selectedProperty?.basicInfo.propertyId,
      });
    }
  }, [currentStep, title, selectedProperty]);

  const handleSubmitWithTracking = (values: any) => {
    if (currentStep) {
      trackMixpanelEvent('flow-step-completed', {
        flowType: 'HomeownerSetup',
        stepEnum: currentStep.stepName,
        stepDisplayName: currentStep.displayName || title,
        homeId: selectedProperty?.basicInfo.propertyId,
      });
    }

    return onSubmit(values);
  };

  return (
    <FormLayout>
      <div className="mb-lg">
        {Boolean(!loading && !stepFormLayout && progressItems.length) && (
          <ProgressBar items={items} />
        )}
      </div>
      {bannerStep && <div className="mb-xl">{bannerStep}</div>}
      {showUnitAddress && units?.length > 1 && (
        <div className="mb-xs">
          <UnitAddressTitle address={unitAddress} />
        </div>
      )}
      <FormLayoutHeader title={title} subtitle={subTitle} pretitle={preTitle} />
      <FormLayout>
        {form || (
          <Form
            mutators={{
              ...arrayMutators,
              ...mutators,
            }}
            initialValuesEqual={isEqual}
            initialValues={initialValues}
            validate={customValidation}
            onSubmit={handleSubmitWithTracking}
            getForm={getForm}
            customError={customError}
            getFormBottomBar={(_formProps: any, nextButtonProps: any) => (
              <FormLayout.BottomBar
                ctaProps={{
                  label: isLastStep() ? doneLabel : nextLabel,
                  ...ctaProps,
                }}
                nextButtonWrapperProps={nextButtonProps}
                {...(handleBackStep && {
                  previousStepProps: {
                    onClick: handleBackStep,
                    to: null,
                  },
                })}
              />
            )}
          />
        )}
      </FormLayout>
      <Space value={SPACE_TYPES.XXXL} />
    </FormLayout>
  );
}
